<template>
  <div class="location-map"></div>
</template>

<script>
import L from "../features/map/leaflet"
import {
  LABELS_ATTRIBUTION,
  LABELS_OPACITY,
  LABELS_PANE,
  LABELS_PANE_Z_INDEX,
  LABELS_SUBDOMAINS,
  LABELS_TILE_URL,
  WATERCOLOR_ATTRIBUTION,
  WATERCOLOR_MAX_ZOOM,
  WATERCOLOR_MIN_ZOOM,
  WATERCOLOR_TILE_URL,
} from "../features/map/basemap"

// A map that shows exactly one place: the artwork page and the location step
// of the upload wizards. It replaces the single-marker GmapMap blocks, which
// each billed Google for a map load.
export default {
  name: "LocationMap",
  props: {
    // Where the map looks.
    center: {
      type: Object,
      required: true,
    },
    // Where the pin is. Defaults to the center, which is what every caller
    // wanted so far.
    position: {
      type: Object,
      default: null,
    },
    zoom: {
      type: Number,
      default: 14,
    },
    iconUrl: {
      type: String,
      default: () => require("../assets/red.png"),
    },
  },
  computed: {
    pin() {
      return this.position || this.center
    },
  },
  mounted() {
    this.map = L.map(this.$el, {
      center: this.latLng(this.center),
      zoom: this.zoom,
      minZoom: WATERCOLOR_MIN_ZOOM,
      maxZoom: WATERCOLOR_MAX_ZOOM,
      // A location preview is not for exploring; the wheel should scroll the page.
      scrollWheelZoom: false,
    })

    L.tileLayer(WATERCOLOR_TILE_URL, {
      minZoom: WATERCOLOR_MIN_ZOOM,
      maxZoom: WATERCOLOR_MAX_ZOOM,
      attribution: WATERCOLOR_ATTRIBUTION,
      crossOrigin: false,
    }).addTo(this.map)

    // Street labels, with no way to turn them off. This map answers exactly
    // one question -- where is this thing -- and the watercolor tiles carry no
    // street names, so labels are never the unwanted half of the picture here.
    // The main map earns a toggle because people also browse it for the art.
    this.map.createPane(LABELS_PANE)
    const labelsPane = this.map.getPane(LABELS_PANE)
    // Above the watercolor tiles (200), below the pin (marker pane, 600), so a
    // street name can never draw over the one thing this map exists to show.
    labelsPane.style.zIndex = LABELS_PANE_Z_INDEX
    labelsPane.style.opacity = LABELS_OPACITY
    // Nothing in the overlay is clickable, and this map does not even take a
    // wheel (scrollWheelZoom is off); labels must not swallow a drag either.
    labelsPane.style.pointerEvents = "none"

    L.tileLayer(LABELS_TILE_URL, {
      pane: LABELS_PANE,
      subdomains: LABELS_SUBDOMAINS,
      minZoom: WATERCOLOR_MIN_ZOOM,
      maxZoom: WATERCOLOR_MAX_ZOOM,
      attribution: LABELS_ATTRIBUTION,
    }).addTo(this.map)

    this.marker = L.marker(this.latLng(this.pin), {
      icon: L.icon({
        iconUrl: this.iconUrl,
        iconSize: [30, 45],
        iconAnchor: [15, 45],
      }),
      interactive: false,
      keyboard: false,
    }).addTo(this.map)

    // The art page renders one of these hidden (md:hidden / hidden md:block),
    // and Leaflet sizes itself once at mount. Re-measure when the viewport
    // changes so the map that becomes visible is not a 0x0 canvas.
    this.onResize = () => this.map && this.map.invalidateSize()
    window.addEventListener("resize", this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize)
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  },
  methods: {
    latLng(point) {
      return [parseFloat(point.lat), parseFloat(point.lng)]
    },
  },
  watch: {
    center: {
      deep: true,
      handler(c) {
        if (this.map && c) this.map.setView(this.latLng(c), this.map.getZoom())
      },
    },
    pin: {
      deep: true,
      handler(p) {
        if (this.marker && p) this.marker.setLatLng(this.latLng(p))
      },
    },
  },
}
</script>

<style scoped>
.location-map {
  width: 100%;
  height: 100%;
  background: #e9e4d6;
}
</style>
